import { motionAPI } from "@blings/blings-player/editor-tools";
import type { MotionAddLayerConfig } from "@blings/blings-player/editor-tools";
import type { Animation } from "@blings/blings-player";
import type { Font } from "@blings/blings-player/lib/src/libs/l/types/text";

export type PlaygroundAddLayerKind =
  | "text"
  | "button"
  | "square"
  | "circle"
  | "image"
  | "video"
  | "audio"
  | "background";

type AnimationFont = Record<string, unknown>;

type AnimationDraft = {
  w?: number;
  h?: number;
  fr?: number;
  ip?: number;
  op?: number;
  layers?: unknown[];
  fonts?: {
    list?: unknown[];
  };
};

type BuildPlaygroundAddLayerConfigArgs = {
  kind: PlaygroundAddLayerKind;
  animation: AnimationDraft;
  currentFrame: number;
};

const DEFAULT_LAYER_DURATION_SECONDS = 3;
const DEFAULT_FRAME_RATE = 30;
const DEFAULT_COMPOSITION_WIDTH = 1920;
const DEFAULT_COMPOSITION_HEIGHT = 1080;
const DEFAULT_SHAPE_SIZE = 200;
const DEFAULT_TEXT_BOX_WIDTH = 360;
const DEFAULT_TEXT_BOX_HEIGHT = 120;
const DEFAULT_IMAGE_SOURCE_SIZE = 512;
const DEFAULT_IMAGE_DISPLAY_SIZE = 240;
const BUTTON_PRECOMP_WIDTH = 360;
const BUTTON_PRECOMP_HEIGHT = 120;
const BUTTON_RECT_WIDTH = 320;
const BUTTON_RECT_HEIGHT = 72;
const BUTTON_RECT_ROUNDNESS = 20;
const BUTTON_TEXT_BOX_WIDTH = 280;
const BUTTON_TEXT_BOX_HEIGHT = 48;
const BUTTON_FONT_SIZE = 34;
const FALLBACK_FONT_ASCENT = 75;
const DEFAULT_FONT_ORIGIN: Font["origin"] = 0;
const DEFAULT_TEXT_LINE_HEIGHT_MULTIPLIER = 1.2;
const BLACK_TEXT_COLOR = [0, 0, 0];
const WHITE_TEXT_COLOR = [1, 1, 1];
const BLACK_SHAPE_STROKE_COLOR = [0, 0, 0, 1];
const LIGHT_GRAY_SHAPE_FILL_COLOR = [0.82, 0.82, 0.82, 1];
const BUTTON_TEXT_COLOR = [0.08, 0.16, 0.27];
const BUTTON_FILL_COLOR = [0.78, 0.7, 0.58, 1];
const BUTTON_STROKE_COLOR = [0.08, 0.16, 0.27, 1];
const DARK_GRAY_BACKGROUND_COLOR = "#2f2f2f";
const DEFAULT_BUTTON_FONT: Font = {
  fFamily: "Arial",
  fName: "ArialMT",
  fStyle: "Regular",
  fWeight: "400",
  origin: DEFAULT_FONT_ORIGIN,
  fClass: "",
  ascent: FALLBACK_FONT_ASCENT,
};

export const DEFAULT_IMAGE_LAYER_URL =
  "https://app.blings.io/assets/3d_render-CRGTD_bA.webp";
export const DEFAULT_WIDE_VIDEO_LAYER_URL =
  "https://assets.blings.io/platform/demo-media/flamingo-wide.mp4";
export const DEFAULT_TALL_VIDEO_LAYER_URL =
  "https://assets.blings.io/platform/demo-media/flamingo-tall.mp4";
export const DEFAULT_AUDIO_LAYER_URL =
  "https://samplelib.com/mp3/sample-15s.mp3";

const STARTER_SCENE_WIDTH = 1080;
const STARTER_SCENE_HEIGHT = 1920;
const STARTER_SCENE_DURATION_SECONDS = 5;
const STARTER_TEXT_FONT_SIZE = 96;
const STARTER_TEXT_BOX_WIDTH = 720;
const STARTER_TEXT_BOX_HEIGHT = 180;
const STARTER_TEXT_ANIMATION_FRAMES = 24;
const STARTER_TEXT_START_Y_OFFSET = 36;

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function isFontOrigin(value: unknown): value is Font["origin"] {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

function normalizeDocumentFont(font: AnimationFont): Font | undefined {
  const fFamily = stringValue(font.fFamily);
  const fName = stringValue(font.fName);
  if (!fFamily || !fName) {
    return undefined;
  }

  const fPath = stringValue(font.fPath);

  return {
    fFamily,
    fName,
    fStyle: stringValue(font.fStyle) ?? "Regular",
    fWeight: stringValue(font.fWeight) ?? "400",
    origin: isFontOrigin(font.origin) ? font.origin : DEFAULT_FONT_ORIGIN,
    fClass: stringValue(font.fClass) ?? "",
    ...(fPath ? { fPath } : {}),
    ascent:
      typeof font.ascent === "number" &&
      Number.isFinite(font.ascent) &&
      font.ascent > 0
        ? font.ascent
        : FALLBACK_FONT_ASCENT,
  };
}

function getDefaultTextLineHeight(fontSize: number): number {
  return Math.round(fontSize * DEFAULT_TEXT_LINE_HEIGHT_MULTIPLIER * 100) / 100;
}

function getCompositionSize(
  animation: AnimationDraft
): {
  width: number;
  height: number;
} {
  return {
    width: finiteNumber(animation.w) ?? DEFAULT_COMPOSITION_WIDTH,
    height: finiteNumber(animation.h) ?? DEFAULT_COMPOSITION_HEIGHT,
  };
}

function getCompositionFrameRange(
  animation: AnimationDraft
): {
  startFrame: number;
  endFrame: number;
} {
  const fallbackDurationFrames = Math.max(
    1,
    Math.round(
      (finiteNumber(animation.fr) ?? DEFAULT_FRAME_RATE) *
        DEFAULT_LAYER_DURATION_SECONDS
    )
  );
  const startFrame = Math.round(finiteNumber(animation.ip) ?? 0);
  const endFrame = Math.max(
    startFrame + 1,
    Math.round(
      finiteNumber(animation.op) ?? startFrame + fallbackDurationFrames
    )
  );

  return { startFrame, endFrame };
}

export function getPlaygroundAddLayerTiming(
  animation: AnimationDraft,
  currentFrame: number
): { startFrame: number; durationFrames: number } {
  const frameRate = Math.max(
    1,
    Math.round(finiteNumber(animation.fr) ?? DEFAULT_FRAME_RATE)
  );
  const durationFrames = Math.max(
    1,
    Math.round(frameRate * DEFAULT_LAYER_DURATION_SECONDS)
  );
  const {
    startFrame: compositionStart,
    endFrame: compositionEnd,
  } = getCompositionFrameRange(animation);
  const latestStartFrame = Math.max(compositionStart, compositionEnd - 1);
  const startFrame = Math.min(
    latestStartFrame,
    Math.max(
      compositionStart,
      Math.round(finiteNumber(currentFrame) ?? compositionStart)
    )
  );

  return {
    startFrame,
    durationFrames: Math.min(durationFrames, compositionEnd - startFrame),
  };
}

export function getPlaygroundFullSceneTiming(
  animation: AnimationDraft
): {
  startFrame: number;
  durationFrames: number;
} {
  const { startFrame, endFrame } = getCompositionFrameRange(animation);
  return {
    startFrame,
    durationFrames: endFrame - startFrame,
  };
}

function getCenteredPosition(animation: AnimationDraft): number[] {
  const { width, height } = getCompositionSize(animation);
  return [width / 2, height / 2, 0];
}

function getDefaultVideoAsset(
  animation: AnimationDraft
): {
  width: number;
  height: number;
  name: string;
  url: string;
} {
  const { width, height } = getCompositionSize(animation);
  const isTallComposition = height > width;

  return {
    width,
    height,
    name: isTallComposition ? "Flamingo Tall Video" : "Flamingo Wide Video",
    url: isTallComposition
      ? DEFAULT_TALL_VIDEO_LAYER_URL
      : DEFAULT_WIDE_VIDEO_LAYER_URL,
  };
}

function getFirstDocumentFont(animation: AnimationDraft): Font | undefined {
  const font = animation.fonts?.list?.[0];
  if (!isRecord(font)) {
    return undefined;
  }

  return normalizeDocumentFont(font);
}

function getTextBoxSize(animation: AnimationDraft): [number, number] {
  const { width } = getCompositionSize(animation);
  const widthWithMargin = Math.max(120, width - 48);
  return [
    Math.min(DEFAULT_TEXT_BOX_WIDTH, widthWithMargin),
    DEFAULT_TEXT_BOX_HEIGHT,
  ];
}

function getBaseConfig({
  animation,
  currentFrame,
}: Pick<BuildPlaygroundAddLayerConfigArgs, "animation" | "currentFrame">): {
  timing: { startFrame: number; durationFrames: number };
  transform: { position: number[] };
  order: { toIndex: number };
} {
  return {
    timing: getPlaygroundAddLayerTiming(animation, currentFrame),
    transform: {
      position: getCenteredPosition(animation),
    },
    order: {
      toIndex: 0,
    },
  };
}

function getBottomLayerOrder(animation: AnimationDraft): { toIndex: number } {
  return {
    toIndex: animation.layers?.length ?? 0,
  };
}

function buildButtonPrecompAnimation(animation: AnimationDraft): Animation {
  const font = getFirstDocumentFont(animation) ?? DEFAULT_BUTTON_FONT;
  const lineHeight = getDefaultTextLineHeight(BUTTON_FONT_SIZE);
  const halfWidth = BUTTON_PRECOMP_WIDTH / 2;
  const halfHeight = BUTTON_PRECOMP_HEIGHT / 2;
  const durationFrames = getPlaygroundAddLayerTiming(animation, 0)
    .durationFrames;
  const buttonAnimation = {
    v: "5.7.4",
    w: BUTTON_PRECOMP_WIDTH,
    h: BUTTON_PRECOMP_HEIGHT,
    fr: finiteNumber(animation.fr) ?? DEFAULT_FRAME_RATE,
    ip: 0,
    op: durationFrames,
    assets: [],
    fonts: {
      list: [font],
    },
    layers: [],
  } as Animation;

  const buttonFill = motionAPI.apply.addLayer(buttonAnimation, {
    kind: "rectangle",
    name: "Button Fill",
    timing: { startFrame: 0, durationFrames },
    transform: {
      position: [halfWidth, halfHeight, 0],
    },
    order: {
      toIndex: 0,
    },
    shape: {
      size: [BUTTON_RECT_WIDTH, BUTTON_RECT_HEIGHT],
      fillColor: BUTTON_FILL_COLOR,
      strokeColor: BUTTON_STROKE_COLOR,
      strokeWidth: 2,
      roundness: BUTTON_RECT_ROUNDNESS,
    },
  });

  const buttonText = motionAPI.apply.addLayer(buttonFill.animation, {
    kind: "text",
    name: "Button Text",
    timing: { startFrame: 0, durationFrames },
    transform: {
      position: [halfWidth, halfHeight, 0],
    },
    order: {
      toIndex: 0,
    },
    text: {
      value: "Button",
      font,
      fontFamily: font.fFamily,
      fontName: font.fName,
      fontSize: BUTTON_FONT_SIZE,
      lineHeight,
      fillColor: BUTTON_TEXT_COLOR,
      justification: 2,
      verticalAlign: 1,
      boxSize: [BUTTON_TEXT_BOX_WIDTH, BUTTON_TEXT_BOX_HEIGHT],
      boxPosition: [-BUTTON_TEXT_BOX_WIDTH / 2, -BUTTON_TEXT_BOX_HEIGHT / 2],
    },
  });

  return buttonText.animation;
}

function buildAnimatedOpacity(startFrame: number, endFrame: number) {
  return {
    a: 1,
    k: [
      {
        i: { x: [0.667], y: [1] },
        o: { x: [0.333], y: [0] },
        t: startFrame,
        s: [0],
      },
      {
        t: endFrame,
        s: [100],
      },
    ],
    ix: 11,
  };
}

function buildAnimatedPosition(
  startPosition: number[],
  endPosition: number[],
  startFrame: number,
  endFrame: number
) {
  return {
    a: 1,
    k: [
      {
        i: { x: 0.667, y: 1 },
        o: { x: 0.333, y: 0 },
        t: startFrame,
        s: startPosition,
        e: endPosition,
        to: [0, -STARTER_TEXT_START_Y_OFFSET / 6, 0],
        ti: [0, STARTER_TEXT_START_Y_OFFSET / 6, 0],
      },
      {
        t: endFrame,
        s: endPosition,
      },
    ],
    ix: 2,
    l: 2,
  };
}

export function buildStarterSceneAnimation(sceneName = "New scene"): Animation {
  const durationFrames = DEFAULT_FRAME_RATE * STARTER_SCENE_DURATION_SECONDS;
  const animation = {
    v: "5.7.4",
    fr: DEFAULT_FRAME_RATE,
    ip: 0,
    op: durationFrames,
    w: STARTER_SCENE_WIDTH,
    h: STARTER_SCENE_HEIGHT,
    nm: sceneName,
    ddd: 0,
    assets: [],
    fonts: {
      list: [DEFAULT_BUTTON_FONT],
    },
    layers: [],
  } as Animation;

  const backgroundResult = motionAPI.apply.addLayer(
    animation,
    buildPlaygroundAddLayerConfig({
      kind: "background",
      animation,
      currentFrame: 0,
    })
  );
  const textPosition = getCenteredPosition(backgroundResult.animation);
  const textResult = motionAPI.apply.addLayer(backgroundResult.animation, {
    kind: "text",
    name: "Text",
    timing: getPlaygroundFullSceneTiming(backgroundResult.animation),
    transform: {
      position: textPosition,
    },
    order: {
      toIndex: 0,
    },
    text: {
      value: sceneName,
      font: DEFAULT_BUTTON_FONT,
      fontFamily: DEFAULT_BUTTON_FONT.fFamily,
      fontName: DEFAULT_BUTTON_FONT.fName,
      fontSize: STARTER_TEXT_FONT_SIZE,
      lineHeight: getDefaultTextLineHeight(STARTER_TEXT_FONT_SIZE),
      fillColor: WHITE_TEXT_COLOR,
      justification: 2,
      verticalAlign: 1,
      boxSize: [STARTER_TEXT_BOX_WIDTH, STARTER_TEXT_BOX_HEIGHT],
      boxPosition: [-STARTER_TEXT_BOX_WIDTH / 2, -STARTER_TEXT_BOX_HEIGHT / 2],
    },
  });

  const textLayer = textResult.layer as
    | {
        ks?: {
          o?: unknown;
          p?: unknown;
        };
      }
    | undefined;
  if (textLayer?.ks) {
    const startPosition = [
      textPosition[0],
      textPosition[1] + STARTER_TEXT_START_Y_OFFSET,
      textPosition[2],
    ];
    textLayer.ks.o = buildAnimatedOpacity(0, STARTER_TEXT_ANIMATION_FRAMES);
    textLayer.ks.p = buildAnimatedPosition(
      startPosition,
      textPosition,
      0,
      STARTER_TEXT_ANIMATION_FRAMES
    );
  }

  return textResult.animation;
}

export function buildPlaygroundAddLayerConfig({
  kind,
  animation,
  currentFrame,
}: BuildPlaygroundAddLayerConfigArgs): MotionAddLayerConfig {
  if (kind === "background") {
    const { width, height } = getCompositionSize(animation);

    return {
      timing: getPlaygroundFullSceneTiming(animation),
      transform: {
        position: getCenteredPosition(animation),
      },
      order: getBottomLayerOrder(animation),
      kind: "solid",
      name: "Background",
      solid: {
        color: DARK_GRAY_BACKGROUND_COLOR,
        width,
        height,
      },
    };
  }

  if (kind === "video") {
    const videoAsset = getDefaultVideoAsset(animation);

    return {
      timing: getPlaygroundFullSceneTiming(animation),
      transform: {
        position: getCenteredPosition(animation),
      },
      order: getBottomLayerOrder(animation),
      kind: "video",
      name: "Video",
      asset: {
        id: "default_video_asset",
        name: videoAsset.name,
        url: videoAsset.url,
        width: videoAsset.width,
        height: videoAsset.height,
        fileExtension: "mp4",
        preserveAspectRatio: "xMidYMid meet",
      },
    };
  }

  if (kind === "audio") {
    return {
      timing: getPlaygroundFullSceneTiming(animation),
      order: getBottomLayerOrder(animation),
      kind: "audio",
      name: "Background Music",
      asset: {
        id: "default_audio_asset",
        name: "Demo Background Music",
        url: DEFAULT_AUDIO_LAYER_URL,
        fileExtension: "mp3",
      },
    };
  }

  const baseConfig = getBaseConfig({ animation, currentFrame });

  if (kind === "button") {
    return {
      ...baseConfig,
      kind: "animation",
      name: "Button",
      animation: buildButtonPrecompAnimation(animation),
      asset: {
        id: "default_button_precomp",
        name: "Button",
        preserveAspectRatio: "xMidYMid meet",
      },
    };
  }

  if (kind === "text") {
    const font = getFirstDocumentFont(animation);
    const [boxWidth, boxHeight] = getTextBoxSize(animation);
    const fontSize = 72;

    return {
      ...baseConfig,
      kind: "text",
      name: "Text",
      text: {
        value: "Text",
        ...(font ? { font } : {}),
        fontFamily: font?.fFamily,
        fontName: font?.fName,
        fontSize,
        lineHeight: getDefaultTextLineHeight(fontSize),
        fillColor: BLACK_TEXT_COLOR,
        justification: 2,
        verticalAlign: 1,
        boxSize: [boxWidth, boxHeight],
        boxPosition: [-boxWidth / 2, -boxHeight / 2],
      },
    };
  }

  if (kind === "image") {
    const imageScale =
      (DEFAULT_IMAGE_DISPLAY_SIZE / DEFAULT_IMAGE_SOURCE_SIZE) * 100;

    return {
      ...baseConfig,
      kind: "image",
      name: "Image",
      asset: {
        id: "default_image_asset",
        name: "Default Image",
        url: DEFAULT_IMAGE_LAYER_URL,
        width: DEFAULT_IMAGE_SOURCE_SIZE,
        height: DEFAULT_IMAGE_SOURCE_SIZE,
        fileExtension: "webp",
        preserveAspectRatio: "xMidYMid meet",
      },
      transform: {
        ...baseConfig.transform,
        scale: imageScale,
      },
    };
  }

  return {
    ...baseConfig,
    kind: kind === "square" ? "rectangle" : "circle",
    name: kind === "square" ? "Square" : "Circle",
    shape: {
      size: [DEFAULT_SHAPE_SIZE, DEFAULT_SHAPE_SIZE],
      fillColor: LIGHT_GRAY_SHAPE_FILL_COLOR,
      strokeColor: BLACK_SHAPE_STROKE_COLOR,
      strokeWidth: 1,
    },
  };
}
